import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Spinner, FloppyDisk, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

function Editor({ page, onSaved }) {
  const [title, setTitle] = useState(page.title || "");
  const [body, setBody] = useState(page.body || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/legal/${page.slug}`, { title, body });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid={`legal-editor-${page.slug}`}>
      <div className="flex items-center justify-between">
        <a href={`/${page.slug}`} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 hover:underline inline-flex items-center gap-1.5" data-testid={`legal-view-${page.slug}`}>
          <ArrowSquareOut size={15} /> View live page
        </a>
        <Button onClick={save} disabled={saving} className="h-10 gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid={`legal-save-${page.slug}`}>
          {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />} Save
        </Button>
      </div>
      <div>
        <Label className="text-sm font-semibold">Page Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-11 mt-1.5" data-testid={`legal-title-${page.slug}`} />
      </div>
      <div>
        <Label className="text-sm font-semibold">Content</Label>
        <p className="text-xs text-zinc-400 mb-1.5">Start a line with <code className="bg-zinc-100 px-1 rounded">## </code> to make a section heading. Separate paragraphs with a blank line.</p>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={22} className="mt-1.5 font-mono text-sm leading-relaxed" data-testid={`legal-content-${page.slug}`} />
      </div>
    </div>
  );
}

export default function AdminLegal() {
  const { user } = useAuth();
  const [pages, setPages] = useState(null);

  const load = () => api.get("/admin/legal").then(({ data }) => setPages(data)).catch(() => setPages([]));
  useEffect(() => { load(); }, []);

  if (user.role !== "super_admin") {
    return <div className="p-8 text-zinc-500" data-testid="legal-admin-denied">Super admin access only.</div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-legal-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Legal Pages</h1>
        <p className="text-zinc-500 text-sm mt-1">Edit your public Privacy Policy and Terms of Service. Changes go live immediately.</p>
      </div>
      {!pages ? (
        <div className="py-20 text-center"><Spinner size={28} className="animate-spin text-emerald-600 mx-auto" /></div>
      ) : (
        <Tabs defaultValue="privacy" className="max-w-3xl">
          <TabsList>
            {pages.map((p) => <TabsTrigger key={p.slug} value={p.slug} data-testid={`legal-tab-${p.slug}`}>{p.title}</TabsTrigger>)}
          </TabsList>
          {pages.map((p) => (
            <TabsContent key={p.slug} value={p.slug} className="mt-4">
              <Editor page={p} onSaved={load} />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
